import { Layout } from "@/components/Layout";
import { useState, useEffect, useRef } from "react";
import { Clinician } from "@/types";
import { useAuthInfo } from "@propelauth/react";
import {
  getClinicians,
  updateClinician,
  getClinicianSignature,
  getCliniciansNotSetup,
} from "@/utils/apiCalls";
import { toast } from "sonner";
import { DataTable } from "@/components/table/Table";
import { SearchFilter } from "@/components/table/SelectFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UploadIcon, XIcon, PlusIcon } from "lucide-react";

export const AdminCliniciansPage = () => {
  const authInfo = useAuthInfo();
  const [clinicians, setClinicians] = useState<Clinician[]>([]);
  const [cliniciansNotSetup, setCliniciansNotSetup] = useState<
    { id: string; email: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedClinician, setSelectedClinician] = useState<Clinician | null>(
    null
  );
  const [formData, setFormData] = useState({
    name: "",
    short_name: "",
    specialty: "psychiatry",
  });
  const [addFormData, setAddFormData] = useState({
    clinician_id: "",
    name: "",
    short_name: "",
    specialty: "psychiatry",
  });
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signatureFileName, setSignatureFileName] = useState<string>("");
  const [signaturePreview, setSignaturePreview] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      const [cliniciansResponse, notSetupResponse] = await Promise.all([
        getClinicians(authInfo.accessToken ?? null),
        getCliniciansNotSetup(authInfo.accessToken ?? null),
      ]);

      if (cliniciansResponse) {
        setClinicians(cliniciansResponse);
      } else {
        toast.error("Failed to fetch clinicians");
      }

      if (notSetupResponse) {
        setCliniciansNotSetup(notSetupResponse);
      }

      setIsLoading(false);
    };

    fetchData();
  }, [authInfo.accessToken]);

  const handleEdit = async (clinician: Clinician) => {
    setSelectedClinician(clinician);
    setFormData({
      name: clinician.name,
      short_name: clinician.short_name,
      specialty: clinician.specialty,
    });
    setSignatureFile(null);
    setSignatureFileName("");

    // Fetch the existing signature
    const signatureUrl = await getClinicianSignature(
      clinician.id,
      authInfo.accessToken ?? null
    );
    setSignaturePreview(signatureUrl || "");

    setIsEditDialogOpen(true);
  };

  const handleAdd = () => {
    setAddFormData({
      clinician_id: "",
      name: "",
      short_name: "",
      specialty: "psychiatry",
    });
    setSignatureFile(null);
    setSignatureFileName("");
    setSignaturePreview("");
    setIsAddDialogOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    setSignatureFile(file);
    setSignatureFileName(file.name);

    // Create preview URL for the selected file
    const previewUrl = URL.createObjectURL(file);
    setSignaturePreview(previewUrl);
  };

  const handleRemoveSignature = () => {
    setSignatureFile(null);
    setSignatureFileName("");
    setSignaturePreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClinician) return;

    const response = await updateClinician(
      selectedClinician.id,
      formData.name,
      formData.short_name,
      formData.specialty,
      authInfo.accessToken ?? null,
      signatureFile || undefined
    );

    if (response) {
      setClinicians(
        clinicians.map((c) => (c.id === selectedClinician.id ? response : c))
      );
      setIsEditDialogOpen(false);
      toast.success("Clinician updated successfully");
    } else {
      toast.error("Failed to update clinician");
    }
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addFormData.clinician_id) {
      toast.error("Please select a clinician");
      return;
    }

    const response = await updateClinician(
      addFormData.clinician_id,
      addFormData.name,
      addFormData.short_name,
      addFormData.specialty,
      authInfo.accessToken ?? null,
      signatureFile || undefined
    );

    if (response) {
      setClinicians([...clinicians, response]);
      setIsAddDialogOpen(false);
      toast.success("Clinician added successfully");
    } else {
      toast.error("Failed to add clinician");
    }
  };

  const handleClinicianSelect = (clinicianId: string) => {
    const selected = cliniciansNotSetup.find((c) => c.id === clinicianId);
    if (selected) {
      setAddFormData({
        ...addFormData,
        clinician_id: clinicianId,
        name: selected.email.split("@")[0], // Use email username as default name
        short_name: selected.email.split("@")[0].substring(0, 3).toUpperCase(), // Use first 3 chars as short name
      });
    }
  };

  const filteredClinicians = clinicians.filter(
    (clinician) =>
      clinician.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clinician.short_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      header: "Name",
      accessorKey: "name",
    },
    {
      header: "Short Name",
      accessorKey: "short_name",
    },
    {
      header: "Specialty",
      accessorKey: "specialty",
    },
    {
      header: "Actions",
      cell: ({ row }: { row: { original: Clinician } }) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEdit(row.original)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Layout title="Manage Clinicians">
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Manage Clinicians</h1>
          <div className="flex items-center gap-4">
            <SearchFilter
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder="Search clinicians..."
            />
            <Button onClick={handleAdd}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Clinician
            </Button>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filteredClinicians}
          hidePagination={false}
          enableSorting={true}
        />

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Clinician</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="short_name">Short Name</Label>
                <Input
                  id="short_name"
                  value={formData.short_name}
                  onChange={(e) =>
                    setFormData({ ...formData, short_name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="specialty">Specialty</Label>
                <Select
                  value={formData.specialty}
                  onValueChange={(value) =>
                    setFormData({ ...formData, specialty: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="psychiatry">Psychiatry</SelectItem>
                    <SelectItem value="psychology">Psychology</SelectItem>
                    <SelectItem value="neurology">Neurology</SelectItem>
                    <SelectItem value="internal medicine">
                      Internal Medicine
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="signature">Signature</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <UploadIcon className="w-4 h-4 mr-2" />
                      {signatureFileName || "Upload New Signature"}
                    </Button>
                    {signaturePreview && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handleRemoveSignature}
                      >
                        <XIcon className="w-4 h-4" />
                      </Button>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleFileChange}
                      accept="image/*"
                    />
                  </div>
                  {signaturePreview && (
                    <div className="mt-2 p-2 border rounded-md">
                      <img
                        src={signaturePreview}
                        alt="Signature preview"
                        className="max-h-32 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Clinician</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div>
                <Label htmlFor="clinician_id">Select Clinician</Label>
                <Select
                  value={addFormData.clinician_id}
                  onValueChange={handleClinicianSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a clinician to add" />
                  </SelectTrigger>
                  <SelectContent>
                    {cliniciansNotSetup.length > 0 ? (
                      cliniciansNotSetup.map((clinician) => (
                        <SelectItem key={clinician.id} value={clinician.id}>
                          {clinician.email}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-clinicians" disabled>
                        No clinicians available to add
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="add_name">Name</Label>
                <Input
                  id="add_name"
                  value={addFormData.name}
                  onChange={(e) =>
                    setAddFormData({ ...addFormData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="add_short_name">Short Name</Label>
                <Input
                  id="add_short_name"
                  value={addFormData.short_name}
                  onChange={(e) =>
                    setAddFormData({
                      ...addFormData,
                      short_name: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="add_specialty">Specialty</Label>
                <Select
                  value={addFormData.specialty}
                  onValueChange={(value) =>
                    setAddFormData({ ...addFormData, specialty: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="psychiatry">Psychiatry</SelectItem>
                    <SelectItem value="psychology">Psychology</SelectItem>
                    <SelectItem value="neurology">Neurology</SelectItem>
                    <SelectItem value="internal medicine">
                      Internal Medicine
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="add_signature">Signature</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <UploadIcon className="w-4 h-4 mr-2" />
                      {signatureFileName || "Upload Signature"}
                    </Button>
                    {signaturePreview && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handleRemoveSignature}
                      >
                        <XIcon className="w-4 h-4" />
                      </Button>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleFileChange}
                      accept="image/*"
                    />
                  </div>
                  {signaturePreview && (
                    <div className="mt-2 p-2 border rounded-md">
                      <img
                        src={signaturePreview}
                        alt="Signature preview"
                        className="max-h-32 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Add</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
